import moment from 'moment';
import {useCallback, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {toast} from 'react-toastify';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import Spinner from '@/assets/svg/Spinner';
import {Button, DatePicker, Header} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import DownloadStatementButton from '@/components/Receipt/DownloadStatementButton';
import useUserTransactionList from '@/screens/Transaction/List/useUserTransactionList';
import useUserDetails from '@/screens/User/Details/useUserDetails';
import {transactionService} from '@/services/transactionService';
import type {AfriexAdminTransaction} from '@/types';
import {formatAdminTransaction} from '@/utils/dashboard';
import {getResponseMessage} from '@/utils/errorMessage';

import AccountStatement from './statement';

const AccountStatementPage: React.FC = () => {
  const params = useParams();
  const userId = params?.userId || '';

  const {isLoadingWalletBalances, isLoadingUserDetails, user, walletBalances} =
    useUserDetails(userId);

  const {
    transactionList,
    isLoading,
    pageInfo: {total: listTotal},
    filtersToApply,
    selectAppliedFilters,
    handleApplyFilters,
  } = useUserTransactionList(userId);

  const [data, setData] = useState<AfriexAdminTransaction[]>([]);
  // How many transactions the range holds; the preview shows at most 100 of
  // them and Download has them all.
  const [dataTotal, setDataTotal] = useState(0);
  const [isFiltered, setIsFiltered] = useState<boolean>(false);
  const [fetchingData, setFetchingData] = useState<boolean>(true);

  const getInitialStatement = useCallback(async () => {
    let transactionData: AfriexAdminTransaction[] = transactionList;
    let total = listTotal;
    if (!isFiltered) {
      const {data, total: todayTotal} =
        await transactionService.getUserTransactionList(userId, {
          limit: 100,
          fromDate: moment().startOf('day').toISOString(),
          toDate: moment().endOf('day').toISOString(),
        });

      const filtered = await Promise.all(
        data &&
          data.map(async (transaction) => {
            const at = await formatAdminTransaction(transaction);
            return {
              ...transaction,
              type: at.type,
              adminUser: at.adminUser,
            };
          }),
      );

      transactionData = filtered;
      total = todayTotal;
    }
    setData(transactionData);
    setDataTotal(total ?? transactionData.length);
    setFetchingData(false);
  }, [userId, transactionList, isFiltered, listTotal]);

  useEffect(() => {
    // getInitialStatement has no try/catch of its own, so this is the only
    // place a failed statement load can be reported. It was silent before.
    //
    // NOTE (pre-existing, unfixed): `setFetchingData(false)` also lives at the
    // end of getInitialStatement, so a failure still leaves the page on its
    // spinner forever. Clearing the flag here would change what the screen
    // renders on failure, so it is left alone and only the report is added.
    getInitialStatement().catch((error: unknown) => {
      toast.error(
        getResponseMessage(error) || 'Failed to load the account statement',
      );
    });
  }, [getInitialStatement]);

  // A statement always pulls a full 100-row page (the merchant statement page
  // passes the same limit straight to its service call). Seed it through the
  // hook's own setter at mount instead of mutating `filtersToApply` inside the
  // click handler: the value is in state well before the admin can press
  // Generate, so `handleApplyFilters` still sends `limit: 100`, but the hook's
  // state is no longer written behind React's back. `selectAppliedFilters` is
  // stable and only touches `filtersToApply`, which no fetch callback depends
  // on, so this does not add a request.
  useEffect(() => {
    selectAppliedFilters('limit', 100);
  }, [selectAppliedFilters]);

  // `handleApplyFilters` can now refuse an invalid date range and apply nothing
  // (see hooks/useFilters.ts). That is unreachable here: Generate is gated on
  // `isDateValid` below, which requires both dates and is STRICTER than the
  // hook's own check (`isAfter` versus `isSameOrAfter`), so anything this screen
  // permits the hook accepts.
  //
  // Do not relax `isDateValid` without revisiting this. The flags are set before
  // applying, and `fetchingData` is only cleared by the effect keyed on
  // `transactionList` -- so a refusal would leave the page on its spinner
  // permanently, the same failure mode already noted for a failed load above.
  const fetchStatement = () => {
    setFetchingData(true);
    setIsFiltered(true);
    handleApplyFilters();
  };

  const fromDate = filtersToApply.fromDate
    ? new Date(String(filtersToApply.fromDate))
    : new Date();
  const toDate = filtersToApply.toDate
    ? new Date(String(filtersToApply.toDate))
    : new Date();
  const minDate = new Date(2023, 0, 1);
  const isDateValid =
    fromDate && toDate && moment(toDate).isAfter(moment(fromDate));

  const statementContent = data ? (
    <>
      {!fetchingData && !isLoading && dataTotal > data.length && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Showing the latest {data.length} of {dataTotal} transactions. Download
          has the full statement.
        </p>
      )}
      <AccountStatement
        user={user}
        balances={walletBalances}
        transactions={data}
        dates={[fromDate, toDate]}
        loading={isLoading || fetchingData}
      />
    </>
  ) : null;

  return (
    <div className="mt-10 mb-5 mx-4 md:mx-10">
      <Header title="Account Statement" />
      <div className="flex justify-center items-center flex-col mt-8">
        {isLoadingUserDetails || isLoadingWalletBalances ? (
          <Spinner size={8} className="text-cyan-600" />
        ) : (
          <div className="flex flex-col gap-3">
            {/* Filter Transactions By Date here... */}
            <div className="flex justify-between gap-3 items-center w-680">
              <div className="flex gap-5 items-center">
                <DatePicker
                  label="From Date"
                  selectedDate={fromDate}
                  setSelectedDate={(date) => {
                    selectAppliedFilters('fromDate', date);
                    if (!filtersToApply.toDate) {
                      selectAppliedFilters('toDate', toDate);
                    }
                  }}
                  minDate={minDate}
                  maxDate={new Date()}
                  placeholderText="Select From Date"
                  containerStyle="w-1/4"
                />

                <DatePicker
                  label="To Date"
                  selectedDate={toDate}
                  setSelectedDate={(date) => {
                    selectAppliedFilters('toDate', date);
                    if (!filtersToApply.fromDate) {
                      selectAppliedFilters('fromDate', fromDate);
                    }
                  }}
                  minDate={minDate}
                  maxDate={new Date()}
                  placeholderText="Select To Date"
                  containerStyle="w-1/4"
                />

                <Button
                  onClick={
                    isDateValid
                      ? fetchStatement
                      : () =>
                          toast.error(
                            'From Date can not be higher than To Date',
                          )
                  }
                  colorScheme="cyan"
                  variant="outline"
                  className="self-end"
                >
                  Search
                </Button>
              </div>
              <PermissionsProvider
                permission={AfriexAdminPermission.UserStatementRead}
              >
                <div className="flex flex-col self-end">
                  {/* The range the statement header shows, whole days, as the
                      list hook applies it. */}
                  <DownloadStatementButton
                    fileName="statement"
                    scope={userId}
                    disabled={fetchingData}
                    download={() =>
                      transactionService.downloadUserStatement(userId, {
                        fromDate: moment(fromDate).startOf('day').toISOString(),
                        toDate: moment(toDate).endOf('day').toISOString(),
                      })
                    }
                  />
                </div>
              </PermissionsProvider>
            </div>

            {fetchingData ? (
              <div className="w-full flex justify-center pt-5 sm:pt-10">
                <Spinner size={8} className="text-cyan-600" />
              </div>
            ) : (
              statementContent
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountStatementPage;
